import { LangLocale } from '@myre/common-i18n';

import { TemplateRef } from '@angular/core';
import { AbstractControl, type ValidationErrors } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { type Observable } from 'rxjs';

import { ElementBase, type IElementBase } from '../elements';
import { requiredValidator, type QuestionValidator } from '../validators';

import { QuestionControlService } from './question-control.service';

export enum BuildChangeStrategy {
  // skip initial value change during build
  SkipSelfBuild = 1,
  // skip all value changes during build
  SkipBuild = 2,
  // skip all but self value changes during build
  OnlySelfBuild = 3,
  // don't skip any value change during build
  Classic = 4,
}

export interface IQuestionBase<T> extends IElementBase<T> {
  initialValue?: T;
  defaultValue?: T;
  initialDisabled?: boolean;
  initialKeepAtPositionOnDisabled?: boolean;
  label?: string;
  picto?: string;
  placeholder?: string;
  unit?: string;
  required?: boolean;
  validators?: QuestionValidator<T>[];
  warningValidators?: QuestionValidator<T>[];
  infoValidators?: QuestionValidator<T>[];
  linkedQuestions?: string[];
  apiErrors?: string[];
  readonly?: boolean;
  onValueChange?: (
    value: T,
    elements: Map<string, ElementBase>,
    self: QuestionBase<any>,
    initiator?: ElementBase,
  ) => void;
  updateOn?: 'change' | 'blur' | 'submit';
  type?: string;
  min?: number;
  max?: number;
  strictlyPositive?: boolean;
  actionButton?: {
    icon: string;
    onClick: (element: QuestionBase<T>, value: T) => void;
  };
  headerContentPosition?: 'start' | 'center' | 'end' | 'between';
  secondLabel?: {
    type:
      | 'button'
      | 'label'
      | 'internalLink'
      | 'externalLink'
      | 'info'
      | 'template';
    label?: string;
    icon?: string;
    internalLink?: string;
    externalLink?: string;
    click?: () => any;
    ellipsis?: boolean;
    template?: TemplateRef<any>;
  };
  buildChangeStrategy?: BuildChangeStrategy;
  equalsOverride?: (value: T) => boolean;
  click?: (event: MouseEvent) => any;
  link?: string;
  /**
   * Used mainly by QuestionContainer to ascend child values at same level as questionContainer value
   */
  ascendValuesToRoot?: boolean;
  /**
   * Remove this field from the "equals" calculation of a QuestionGroup or a QuestionContainer.
   * It ensures calculated fields are not taken into account for pristine status
   */
  excludeFromControls?: boolean;
  questionMarkTooltip?: string;
  formatValue?: (value: T) => T;
  value$?: Observable<T>;
}

export type QuestionBaseWithoutType<T> = Omit<IQuestionBase<T>, 'type'>;

export interface SetValueOptions {
  emitEvent?: boolean;
  force?: boolean;
}

export class QuestionBase<T>
  extends ElementBase<T>
  implements IQuestionBase<T>
{
  public label?: string;
  public picto?: string;
  public placeholder?: string;
  public unit?: string;
  public validators: QuestionValidator<T>[] = [];
  public warningValidators: QuestionValidator<T>[] = [];
  public infoValidators: QuestionValidator<T>[] = [];
  public linkedQuestions: string[];
  public apiErrors: string[];
  public readonly = false;
  public questionControlService: QuestionControlService;
  public lang: LangLocale;
  public type?: string = 'text';
  public headerContentPosition?: 'start' | 'center' | 'end' | 'between';
  public actionButton?: {
    icon: string;
    onClick: (element: QuestionBase<T>, value: T) => MouseEvent | void;
  };

  public secondLabel?: {
    type:
      | 'button'
      | 'label'
      | 'internalLink'
      | 'externalLink'
      | 'info'
      | 'template';
    label?: string;
    icon?: string;
    internalLink?: string;
    externalLink?: string;
    click?: () => any;
    ellipsis?: boolean;
    template?: TemplateRef<any>;
  };

  public defaultValue: T;
  public initialValue: T;
  public initialDisabled: boolean;
  public initialKeepAtPositionOnDisabled: boolean;
  public updateOn: 'change' | 'blur' | 'submit';
  public equalsOverride?: (value: T) => boolean;
  public buildChangeStrategy: BuildChangeStrategy;
  public max: number;
  public min: number;
  public strictlyPositive: boolean;
  public link: string;
  public click: (event: MouseEvent) => any;
  public ascendValuesToRoot = false;
  public excludeFromControls = false;
  public questionMarkTooltip: string;
  public value$?: Observable<T>;

  protected _formatValue: (value: T) => T;

  protected _formControl: AbstractControl;
  protected _setValidators: (validators: QuestionValidator<T>[]) => void;
  protected _setWarningValidators: (validators: QuestionValidator<T>[]) => void;

  private _required: boolean;

  constructor(options: IQuestionBase<T>) {
    super(options);
    this.initialValue = cloneDeep(options.initialValue);
    this.defaultValue = cloneDeep(options.defaultValue);
    this.label = options.label || '';
    this.picto = options.picto;
    this.placeholder = options.placeholder || '';
    this.unit = options.unit || '';
    this.validators = options.validators || [];
    this.warningValidators = options.warningValidators || [];
    this.infoValidators = options.infoValidators || [];
    this.linkedQuestions = options.linkedQuestions || [];
    // required should be initialized after validators
    this.required = options.required === true;
    this.readonly = options.readonly === true;
    this.apiErrors = options.apiErrors || [];
    this.initialDisabled = options.initialDisabled === true;
    this.initialKeepAtPositionOnDisabled =
      options.initialKeepAtPositionOnDisabled;
    this.type = options.type;
    this.actionButton = options.actionButton;
    this.secondLabel = options.secondLabel;
    this.min = typeof options.min === 'number' ? options.min : null;
    this.max = typeof options.max === 'number' ? options.max : null;
    this.strictlyPositive = options.strictlyPositive;
    this.ascendValuesToRoot = !!options.ascendValuesToRoot;
    this.questionMarkTooltip = options.questionMarkTooltip;
    this.value$ = options.value$;
    this.headerContentPosition = options.headerContentPosition || 'between';

    this._formatValue = options.formatValue;

    if (this.secondLabel && typeof this.secondLabel.ellipsis !== 'boolean') {
      this.secondLabel.ellipsis = true;
    }

    this.updateOn = options.updateOn || 'change';
    this.buildChangeStrategy =
      options.buildChangeStrategy || BuildChangeStrategy.Classic;
    this.onValueChange = options.onValueChange || (() => {});

    if (options.equalsOverride) {
      this.equals = options.equalsOverride.bind(this);
    }

    this.click = options.click;
    this.link = options.link;

    this.excludeFromControls = options.excludeFromControls === true;
  }

  public getUnit(): string {
    return this.unit;
  }

  public getUnitTag(): string | null {
    return this.getUnit() ?? null;
  }

  public initStart(): void {}

  public get skipInitialValueChange(): boolean {
    return [
      BuildChangeStrategy.SkipSelfBuild,
      BuildChangeStrategy.SkipBuild,
    ].includes(this.buildChangeStrategy);
  }

  public get skipBuildValueChanges(): boolean {
    return [
      BuildChangeStrategy.SkipBuild,
      BuildChangeStrategy.OnlySelfBuild,
    ].includes(this.buildChangeStrategy);
  }

  public labelClick(): void {}

  public registerFormControl(formControl: AbstractControl): void {
    this._formControl = formControl;
  }

  // 'keepHidden' is declared but its value is never read.
  // Fix: Any parameter name starting with _ is exempt from the check.
  // Use _myVariable instead of myvariable to remove this warning
  public getValue(_?: boolean): T {
    return this._formControl.value;
  }

  public equals(value: T): boolean {
    return this.getValue() === value;
  }

  public setValue(
    value: T,
    opts: SetValueOptions = { emitEvent: true, force: false },
  ): void {
    if (!this.equals(value) || opts.force) {
      this._formControl.patchValue(value, opts);
    }
  }

  public get errors(): ValidationErrors | null {
    return this._formControl ? this._formControl.errors : null;
  }

  public get warnings(): ValidationErrors | null {
    return this._formControl ? (this._formControl as any).warnings : null;
  }

  public get required(): boolean {
    return this._required;
  }

  public set required(required: boolean) {
    if (this._required === required) {
      return;
    }

    this._required = required;

    if (required && !this.validators.includes(requiredValidator)) {
      this.validators.push(requiredValidator);
    } else if (!required && this.validators.includes(requiredValidator)) {
      this.validators = this.validators.filter(v => v !== requiredValidator);
    }

    if (this.getFormControl()) {
      this.setValidators(this.validators);
      this.validate(false, false);
    }
  }

  public setRequired(required: boolean): void {
    this.required = required;
  }

  public setErrors(errors: ValidationErrors | null): void {
    this._formControl.setErrors(errors);
  }

  public setWarnings(warnings: ValidationErrors | null): void {
    (this._formControl as any).setWarnings(warnings);
  }

  public setInfo(info: ValidationErrors | null): void {
    (this._formControl as any).setInfo(info);
  }

  public getFormControl(): AbstractControl {
    return this._formControl;
  }

  /**
   * @deprecated
   * Use enable or disable instead
   */
  public override setEnabled(
    enabled: boolean,
    keepAtPosition: boolean = false,
  ): void {
    if (enabled) {
      this.enable();
    } else {
      this.disable(keepAtPosition);
    }
  }

  /**
   * @deprecated
   * Use enable or disable instead
   */
  public override setDisabled(
    disabled: boolean,
    keepAtPosition: boolean = false,
  ): void {
    this.setEnabled(!disabled, keepAtPosition);
  }

  public override enable(): void {
    super.enable();

    // make sure __myre_pristine is explicitly a boolean
    if (typeof (this._formControl as any).__myre_pristine === 'boolean') {
      if ((this._formControl as any).__myre_pristine) {
        this._formControl.markAsPristine();
      } else {
        this._formControl.markAsDirty();
      }
    }

    this._formControl.enable({ onlySelf: true });
    this.update(true);
  }

  public override disable(keepAtPosition: boolean = false): void {
    super.disable(keepAtPosition);
    (this._formControl as any).__myre_pristine = this._formControl.pristine;
    this._formControl.markAsPristine();
    this._formControl.disable({ onlySelf: true });
    this.update(true);
  }

  public override setVisible(visible: boolean): void {
    super.setVisible(visible);
    this.update(true);
  }

  public override get enabled(): boolean {
    return !this.disabled;
  }

  public override get disabled(): boolean {
    return this._formControl
      ? this._formControl.disabled
      : this.initialDisabled;
  }

  public override update(emitEvent: boolean = false): void {
    this._formControl.updateValueAndValidity({ emitEvent });
  }

  public get valid(): boolean {
    return !this._formControl || this._formControl.valid;
  }

  public get pristine(): boolean {
    return !this._formControl || this._formControl.pristine;
  }

  public validate(
    markAsTouched: boolean = false,
    emitEvent: boolean = true,
  ): void {
    this.setValue(this.getValue(), { emitEvent, force: true });

    if (markAsTouched && this._formControl) {
      this._formControl.markAsTouched();
    }

    this.update();
  }

  public registerSetValidators(
    setValidators: (validators: QuestionValidator<T>[]) => void,
  ): void {
    this._setValidators = setValidators;
  }

  public registerSetWarningValidators(
    setValidators: (validators: QuestionValidator<T>[]) => void,
  ): void {
    this._setWarningValidators = setValidators;
  }

  public setValidators(validators: QuestionValidator<T>[]): void {
    if (this._setValidators) {
      this._setValidators(validators);
    }
  }

  public setWarningValidators(validators: QuestionValidator<T>[]): void {
    if (this._setWarningValidators) {
      this._setWarningValidators(validators);
    }
  }

  public getInitialValue(): T {
    return this.initialValue;
  }
}
